import { defineStore } from "pinia";
import { ref } from "vue";

import { apiClient } from "@/client";
import { Mailer, type MailerDTO } from "@/type/api";

export const useStoreMailer = defineStore("mailer", () => {
	// State
	const _all = ref<Mailer[] | null>(null);
	const _byID = ref<Map<string, Mailer>>(new Map());
	const loading = ref(false);
	const ongoingFetch = ref<Promise<Mailer[]> | null>(null);

	// Actions
	async function byID(id: string): Promise<Mailer> {
		const r = _byID.value.get(id);
		if (r) {
			return r;
		}
		return fetchByID(id);
	}
	async function byURI(uri: string): Promise<Mailer> {
		const id = uri.split("/").pop() || "";
		if (!id) {
			throw new Error(`${uri} is not a recognized public report URI`);
		}
		return byID(id);
	}
	/**
	 * The mailers page's whole-collection read through the generic QUERY endpoint
	 * (doc/in-progress/query.md §11 Phase 4): every letter the district's
	 * compliance report requests have produced, newest first — the ordering the
	 * page asked for with an inert `sort=-created` before this slice, and the
	 * envelope's own order now (D15). The rows are the wire shape the legacy list
	 * answered (the district's mailers), so the page's table renders the same
	 * columns.
	 */
	async function fetchAll(): Promise<Mailer[]> {
		loading.value = true;
		try {
			const page = await apiClient.JSONQueryAll<MailerDTO>("/api/mailer");
			const mailers = page.items.map((m) => Mailer.fromJSON(m));
			_all.value = mailers;
			for (const m of mailers) {
				_byID.value.set(m.id, m);
			}
			return mailers;
		} catch (err) {
			console.error("Error loading mailers:", err);
			throw err;
		} finally {
			loading.value = false;
		}
	}
	async function fetchByID(id: string): Promise<Mailer> {
		const uri = `/api/publicreport/${id}`;
		return fetchByURI(uri);
	}
	async function fetchByURI(uri: string): Promise<Mailer> {
		loading.value = true;
		try {
			const response = await fetch(uri);

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			const body: MailerDTO = await response.json();
			const report = Mailer.fromJSON(body);
			_byID.value.set(report.id, report);
			return report;
		} catch (err) {
			console.error("Error loading users:", err);
			throw err;
		} finally {
			loading.value = false;
		}
	}
	async function list(): Promise<Mailer[]> {
		if (_all.value) {
			return _all.value;
		}
		if (ongoingFetch.value !== null) {
			return ongoingFetch.value;
		}
		ongoingFetch.value = fetchAll().finally(() => {
			ongoingFetch.value = null;
		});
		return ongoingFetch.value;
	}
	return {
		// Actions
		byID,
		byURI,
		fetchByID,
		fetchByURI,
		list,
	};
});
